import React, { FunctionComponent, useEffect, useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { FontFamily } from './FontFamily.tsx'
import { fontLoader } from '../../infrastructure/services/fontLoaderService'
import { filterUnsupportedChars } from '../../domain/canvas/services/fontGlyphFilter'
import { useToast } from '../../hooks/useToast'
import { useQueryFonts } from '../hooks/useQueryFonts'
import { sortFonts } from '../../domain/canvas/utils/fontDisplay'

type OnSaveHandlerProps = {
  textContent: string
  textFont: string
  textFontWeight: number
  textFontItalic: boolean
  textSize: number
}
type OnSaveHandler = (props: OnSaveHandlerProps) => void
type OnCancelHandler = () => void

type TextEditorProps = {
  editingTextId: string | null
  isMobile: boolean
  baseColor: string
  initialTextContent: string
  initialTextFont: string
  initialTextFontWeight: number
  initialTextFontItalic: boolean
  initialTextSize: number
  onSave: OnSaveHandler
  onCancel: OnCancelHandler
}

export const TextEditor: FunctionComponent<TextEditorProps> = ({
  editingTextId,
  isMobile,
  baseColor,
  initialTextContent,
  initialTextFont,
  initialTextFontWeight,
  initialTextFontItalic,
  initialTextSize,
  onSave,
  onCancel,
}) => {
  const intl = useIntlayer('textEditor')
  const { showToast } = useToast()
  const fonts = useQueryFonts()
  const [textContent, setTextContent] = useState(initialTextContent)
  const [textFont, setTextFont] = useState(initialTextFont)
  const [textFontWeight, setTextFontWeight] = useState(initialTextFontWeight)
  const [textFontItalic, setTextFontItalic] = useState(initialTextFontItalic)
  const [textSize, setTextSize] = useState(initialTextSize)
  const [showFontError, setShowFontError] = useState(false)
  // Track if user has actually selected a font
  // Consider it selected if: editing existing text OR there's already a valid initial font from defaults
  const [fontManuallySelected, setFontManuallySelected] = useState(
    !!editingTextId || (!!initialTextFont && initialTextFont.trim() !== ''),
  )

  // Pick a sensible default font once the catalog resolves:
  // - First-time use opens with no font (catalog is async, so we can't pick it at click time).
  // - The stored default may also point at a font that's no longer (or never was) in the catalog
  //   — e.g. "Roboto" left behind by the AI agent. In both cases, fall back to the first entry
  //   so the dropdown shows a real preview instead of "Geen voorbeeld".
  // Skipped when editing an existing element: we trust whatever font that element has.
  useEffect(() => {
    if (editingTextId) return
    if (!fonts || fonts.length === 0) return
    const stillValid = textFont && fonts.some((f) => f.name === textFont)
    if (stillValid) return
    const first = sortFonts(fonts)[0]
    setTextFont(first.name)
    setTextFontWeight(first.weight)
    setTextFontItalic(first.italic)
  }, [fonts, textFont, editingTextId])

  const resolveIntlString = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (value && typeof (value as { value?: unknown }).value === 'string') {
      return (value as { value: string }).value
    }
    return String(value ?? '')
  }

  const warnAboutRemoved = (removed: string[]) => {
    if (removed.length === 0) return
    const template = resolveIntlString(intl.unsupportedCharacters)
    const message = template.replace('{chars}', removed.join(' ')).replace('{font}', textFont)
    showToast(message, 'error')
  }

  // Re-filter the current content whenever the font changes — switching to a more restrictive
  // font (e.g. a script font that lacks `&`) must strip newly-unsupported characters too.
  useEffect(() => {
    const supported = fontLoader.getSupportedCodepoints(textFont)
    if (!supported) return
    const { filtered, removed } = filterUnsupportedChars(textContent, supported)
    if (filtered !== textContent) {
      setTextContent(filtered)
      warnAboutRemoved(removed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFont])

  const handleSaveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    // Validate font selection - for new text, user must have manually selected a font
    // This ensures they consciously choose a font rather than relying on defaults
    if (!editingTextId && !fontManuallySelected) {
      setShowFontError(true)
      return
    }

    onSave({
      textContent,
      textFont,
      textFontWeight,
      textFontItalic,
      textSize: Math.round(textSize),
    })
  }

  const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onCancel()
  }

  return (
    <div
      className={`bg-white rounded-lg p-6 shadow-xl mx-4 ${isMobile ? 'max-w-md w-full' : 'max-w-4xl w-full'}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      data-gtm="text-editor-modal"
    >
      <h3 className="text-lg font-semibold mb-4">
        {editingTextId ? intl.editTitle : intl.addTitle}
      </h3>

      <div className={`${isMobile ? '' : 'flex gap-6'}`}>
        {/* Left side - Controls */}
        <div className={`${isMobile ? '' : 'flex-1'}`}>
          {/* Text Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {intl.contentLabel}
            </label>
            <textarea
              value={textContent}
              onChange={(e) => {
                const supported = fontLoader.getSupportedCodepoints(textFont)
                const { filtered, removed } = filterUnsupportedChars(e.target.value, supported)
                setTextContent(filtered)
                warnAboutRemoved(removed)
              }}
              onPaste={(e) => {
                e.preventDefault()
                const pastedText = e.clipboardData.getData('text')
                const supported = fontLoader.getSupportedCodepoints(textFont)
                const { filtered: filteredText, removed } = filterUnsupportedChars(
                  pastedText,
                  supported,
                )
                warnAboutRemoved(removed)
                // Insert filtered text at cursor position
                const target = e.target as HTMLTextAreaElement
                const start = target.selectionStart
                const end = target.selectionEnd
                const newValue =
                  textContent.substring(0, start) + filteredText + textContent.substring(end)
                setTextContent(newValue)
                // Restore cursor position after state update
                setTimeout(() => {
                  target.selectionStart = target.selectionEnd = start + filteredText.length
                }, 0)
              }}
              placeholder={
                (intl.placeholder as any)?.value ||
                (intl.placeholder as any) ||
                'Enter your text here...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={4}
              autoFocus
            />
          </div>

          {/* Font and Size in a row on desktop */}
          <div className={`${isMobile ? '' : 'flex gap-4'}`}>
            {/* Font Selection */}
            <div className="mb-4 flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {intl.fontLabel} <span className="text-red-600">*</span>
              </label>
              <FontFamily
                fontFamily={textFont}
                fontWeight={textFontWeight}
                fontItalic={textFontItalic}
                onChange={(fontFamily, weight, italic) => {
                  setTextFont(fontFamily)
                  setTextFontWeight(weight)
                  setTextFontItalic(italic)
                  // Mark font as manually selected and clear error
                  setFontManuallySelected(true)
                  setShowFontError(false)
                }}
              />
              {showFontError && (
                <p className="mt-2 text-sm text-red-600" data-gtm="font-error-message">
                  {intl.fontRequiredError}
                </p>
              )}
            </div>

            {/* Font Size */}
            <div className="mb-4 flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {intl.fontSizeLabel}: {Math.round(textSize)}mm
              </label>
              <input
                type="range"
                min="5"
                max="100"
                step="1"
                value={Math.round(textSize)}
                onChange={(e) => setTextSize(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Right side - Preview (desktop only) */}
        {!isMobile && (
          <div className="flex-1 mt-7 mb-4">
            <div className="h-full border border-gray-300 rounded-md bg-gray-50 p-6 flex flex-col">
              <p className="text-sm text-gray-700 mb-4">{intl.previewLabel}</p>
              <div className="flex-1 flex items-center justify-center">
                <div
                  style={{
                    fontFamily: textFont,
                    fontSize: `${Math.round(textSize) * 1.5}px`,
                    color: baseColor,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {textContent || intl.previewText}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Preview */}
        {isMobile && textContent && (
          <div className="mb-4 p-4 border border-gray-300 rounded-md bg-gray-50">
            <p className="text-sm text-gray-700 mb-2">{intl.previewLabel}</p>
            <div
              style={{
                fontFamily: textFont,
                fontSize: `${Math.round(textSize) * 0.8}px`,
                color: baseColor,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {textContent}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSaveClick}
          disabled={!textContent.trim()}
          className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors disabled:bg-gray-400"
          data-gtm={editingTextId ? 'update-text-button' : 'add-text-submit-button'}
        >
          {editingTextId ? intl.updateButton : intl.addButton}
        </button>
        <button
          onClick={handleCancelClick}
          className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          data-gtm="cancel-text-button"
        >
          {intl.cancelButton}
        </button>
      </div>
    </div>
  )
}
